import { useEffect, useRef, useState } from 'react'
import { lifeAreaLabel, lifeAreaValue } from '../../utils/taskForm'
import { useLifeAreaOptions } from '../../hooks/useLifeAreas'
import { AddOwnChip } from './AddOwnChip'

/**
 * The life area(s) of any item, edited in place from its summary line
 * (owner 2026-09-05: clickable, one or more, the first is the main one,
 * "selectable and also addable"). Generic over what saves it: the caller
 * passes the current values and an `onSave`; tasks and habits wrap it.
 */
interface Props {
  value: string[]
  onSave: (values: string[]) => Promise<void>
  editable?: boolean
  className?: string
  testId?: string
}

export function LifeAreasPicker({ value, onSave, editable = true, className = '', testId = 'life-areas' }: Props) {
  const current = value || []
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(current)
  const [added, setAdded] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const rootRef = useRef<HTMLSpanElement>(null)
  const { options, remember } = useLifeAreaOptions([...current, ...added])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggle = (v: string) => setDraft((d) => (d.includes(v) ? d.filter((x) => x !== v) : [...d, v]))
  const addOwn = (text: string) => {
    const v = lifeAreaValue(text)
    if (!v) return
    setAdded((a) => (a.includes(v) ? a : [...a, v]))
    setDraft((d) => (d.includes(v) ? d : [...d, v]))
  }

  const save = async () => {
    setBusy(true); setErr(null)
    try {
      await onSave(draft)
      added.forEach(remember)
      setAdded([])
      setOpen(false)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  const text = current.length === 0
    ? 'no life area'
    : current.length === 1 ? lifeAreaLabel(current[0]) : `${lifeAreaLabel(current[0])} +${current.length - 1}`
  const title = current.length > 1 ? current.map(lifeAreaLabel).join(', ') : undefined

  if (!editable) return <span className={className} title={title}>{text}</span>

  return (
    <span ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => { setDraft(current); setOpen((o) => !o) }}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={title || 'Change the life area'}
        className={`rounded px-0.5 underline decoration-dotted underline-offset-2 hover:text-brand ${current.length === 0 ? 'text-muted' : ''}`}
        data-testid={testId}
      >
        {text}
      </button>
      {open && (
        <div role="dialog" aria-label="Life areas" className="absolute left-0 top-full z-30 mt-1 w-80 rounded-lg border border-soft bg-surface p-3 shadow-lg" data-testid={`${testId}-picker`}>
          <p className="text-app-micro text-muted">Pick one or more. The first you pick is the main one. Name your own if none fits.</p>
          <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Life areas">
            {options.map((o) => {
              const on = draft.includes(o.value)
              const primary = draft[0] === o.value
              return (
                <button key={o.value} type="button" aria-pressed={on} onClick={() => toggle(o.value)}
                        className={`rounded-full border px-2.5 py-1 text-app-micro font-medium transition-colors ${
                          on ? 'border-brand bg-brand text-on-brand' : 'border-soft bg-surface text-secondary hover:border-brand'}`}>
                  {o.label}{primary && draft.length > 1 ? ' · main' : ''}
                </button>
              )
            })}
            <AddOwnChip
              onAdd={addOwn}
              placeholder="e.g. Side project"
              chipClassName="rounded-full border border-dashed border-soft bg-surface px-2.5 py-1 text-app-micro font-medium text-secondary transition-colors hover:border-brand"
              inputClassName="w-36 rounded-full border border-soft bg-surface px-2.5 py-1 text-app-micro text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30"
              testId={`${testId}-add`}
            />
          </div>
          {err && <p className="mt-2 text-xs text-bad" role="alert">{err}</p>}
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={() => void save()} disabled={busy} className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-on-brand hover:opacity-90 disabled:opacity-60" data-testid={`${testId}-save`}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="px-2 py-1.5 text-sm text-muted hover:text-secondary">Cancel</button>
          </div>
        </div>
      )}
    </span>
  )
}

export default LifeAreasPicker
